import { useState } from 'react';
import axios from 'axios';
import { Wine, X, Send, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const SommelierWidget = ({ tenantId, lang, tema, t, mostraPrezzi }) => {
  const [aperto, setAperto] = useState(false);
  const [piatto, setPiatto] = useState('');
  const [loading, setLoading] = useState(false);
  const [risposta, setRisposta] = useState(null);
  const [errore, setErrore] = useState(false);

  const chiedi = async () => {
    if (piatto.trim().length < 3 || loading) return;
    setLoading(true);
    setErrore(false);
    try {
      const r = await axios.post(`${API}/api/carta-vini/pubblica/${tenantId}/sommelier`,
        { piatto: piatto.trim(), lang }, { timeout: 45000 });
      setRisposta(r.data);
    } catch (e) {
      setErrore(true);
    }
    setLoading(false);
  };

  const reset = () => { setRisposta(null); setPiatto(''); setErrore(false); };

  return (
    <>
      {/* Pulsante flottante */}
      {!aperto && (
        <button onClick={() => setAperto(true)} data-testid="sommelier-fab"
          style={{
            position: 'fixed', bottom: 22, right: 20, zIndex: 40,
            width: 58, height: 58, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: tema.accento, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 22px rgba(0,0,0,0.35)', transition: 'transform 0.2s'
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          title={t.sommTitolo}>
          <Wine size={26} />
        </button>
      )}

      {/* Pannello */}
      {aperto && (
        <div data-testid="sommelier-panel" style={{
          position: 'fixed', bottom: 0, right: 0, left: 0, zIndex: 45,
          display: 'flex', justifyContent: 'flex-end', pointerEvents: 'none', padding: '0 0 0 0'
        }}>
          <div style={{
            pointerEvents: 'auto', width: '100%', maxWidth: 420, maxHeight: '78vh', overflowY: 'auto',
            background: tema.card, color: tema.testo, borderRadius: '18px 18px 0 0',
            border: `1px solid ${tema.bordo}`, borderBottom: 'none',
            boxShadow: '0 -8px 40px rgba(0,0,0,0.35)', padding: '18px 20px 24px',
            fontFamily: "'Jost', 'Helvetica Neue', Arial, sans-serif", margin: '0 auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <Wine size={20} color={tema.accento} />
                <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 20, fontWeight: 600, letterSpacing: 1.5 }}>{t.sommTitolo}</span>
              </div>
              <button onClick={() => setAperto(false)} data-testid="sommelier-chiudi"
                style={{ background: 'transparent', border: 'none', color: tema.secondario, cursor: 'pointer', padding: 6 }}>
                <X size={20} />
              </button>
            </div>

            {!risposta && loading && (
              <div style={{ textAlign: 'center', padding: '14px 0 6px' }}>
                <style>{`@keyframes somm-sway { 0%, 100% { transform: rotate(-2.5deg) translateY(0); } 50% { transform: rotate(2.5deg) translateY(-6px); } }`}</style>
                <img src="/sommelier-loader.png" alt="" style={{ height: 110, display: 'block', margin: '0 auto', animation: 'somm-sway 2.4s ease-in-out infinite', transformOrigin: 'bottom center' }} />
                <p style={{ margin: '10px 0 0', fontSize: 13, fontStyle: 'italic', color: tema.secondario }}>{t.sommPensando}</p>
              </div>
            )}

            {!risposta && !loading && (
              <>
                <p style={{ margin: '6px 0 12px', fontSize: 13.5, fontStyle: 'italic', color: tema.secondario }}>{t.sommDomanda}</p>
                <textarea value={piatto} onChange={e => setPiatto(e.target.value)} data-testid="sommelier-input"
                  placeholder={t.sommPlaceholder} rows={2} maxLength={200}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); chiedi(); } }}
                  style={{
                    width: '100%', boxSizing: 'border-box', resize: 'none', fontSize: 14.5, lineHeight: 1.5,
                    padding: '10px 12px', borderRadius: 10, border: `1px solid ${tema.bordo}`,
                    background: tema.bg, color: tema.testo, fontFamily: 'inherit', outline: 'none'
                  }} />
                {errore && <p style={{ color: '#c0392b', fontSize: 12.5, margin: '8px 0 0' }}>{t.sommErrore}</p>}
                <button onClick={chiedi} disabled={piatto.trim().length < 3} data-testid="sommelier-invia"
                  style={{
                    width: '100%', marginTop: 12, padding: '11px 0', borderRadius: 999, border: 'none',
                    background: tema.accento, color: '#fff', fontSize: 14.5, letterSpacing: 1, cursor: 'pointer',
                    opacity: piatto.trim().length < 3 ? 0.55 : 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'inherit'
                  }}>
                  <Send size={16} />
                  {t.sommBtn}
                </button>
              </>
            )}

            {risposta && (
              <div data-testid="sommelier-risposta">
                {risposta.messaggio && (
                  <p style={{ fontSize: 14, fontStyle: 'italic', lineHeight: 1.55, color: tema.testo, margin: '8px 0 14px' }}>
                    “{risposta.messaggio}”
                  </p>
                )}
                {risposta.consigli.map((c, i) => {
                  const fascia = c.fascia || 'prestigio';
                  const primaDellaFascia = i === 0 || (risposta.consigli[i - 1].fascia || 'prestigio') !== fascia;
                  const isPrestigio = fascia === 'prestigio';
                  return (
                    <div key={c.vino.id}>
                      {primaDellaFascia && (
                        <p data-testid={`sommelier-fascia-${fascia}`} style={{
                          margin: '12px 0 8px', fontSize: 11, letterSpacing: 2, textTransform: 'uppercase',
                          color: isPrestigio ? tema.accento : tema.secondario, fontWeight: 600
                        }}>
                          {isPrestigio ? `★ ${t.sommPrestige}` : t.sommValue}
                        </p>
                      )}
                      <div data-testid="sommelier-consiglio" style={{
                        padding: '12px 14px', marginBottom: 10, borderRadius: 12,
                        border: `1px solid ${isPrestigio ? tema.accento : tema.bordo}`,
                        background: isPrestigio ? `${tema.accento}12` : 'transparent'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
                          <span style={{ fontWeight: 600, fontSize: 14.5 }}>
                            {c.vino.nome}{c.vino.annata ? ` ${c.vino.annata}` : ''}
                          </span>
                          {mostraPrezzi && c.vino.prezzo_vendita > 0 && (
                            <span style={{ color: tema.accento, fontWeight: 600, whiteSpace: 'nowrap', fontSize: 14 }}>
                              € {Math.round(c.vino.prezzo_vendita)}
                            </span>
                          )}
                        </div>
                        {c.vino.cantina && (
                          <p style={{ margin: '2px 0 0', fontSize: 12.5, fontStyle: 'italic', color: tema.accento }}>{c.vino.cantina}</p>
                        )}
                        {c.motivo && (
                          <p style={{ margin: '6px 0 0', fontSize: 12.5, lineHeight: 1.5, color: tema.secondario }}>{c.motivo}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
                <button onClick={reset} data-testid="sommelier-altro"
                  style={{
                    width: '100%', marginTop: 4, padding: '10px 0', borderRadius: 999,
                    border: `1px solid ${tema.bordo}`, background: 'transparent', color: tema.secondario,
                    fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, fontFamily: 'inherit'
                  }}>
                  <RotateCcw size={14} /> {t.sommAltro}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
